/**
 * Admin Web shell (S111; M12). The operator console's composition of `@vello/app-shell` + `@vello/api-client`.
 * It authenticates with a credential + MFA (AAL2, S015A) via `OperatorAuthApi`, never consumer OTP; a consumer
 * session can never open an operator route (deny-by-default realm isolation from `@vello/app-shell`). It adds
 * an operator scope (country/state/city) the operator switches within their grants, and an inactivity/session-expiry
 * gate that forces re-auth. The web layer (router, real secure storage) is a thin adapter; the shell logic is
 * deterministic and tested.
 *
 * **There is no ADMIN realm.** The Admin console rides an **operator realm** (MERCHANT or BUSINESS): the
 * operator authenticates → an AAL2 operator session, and *platform-admin authority* is the server-side scoped
 * `admin_role_grants` that `authorizeOperator` checks (with AAL2) on every privileged `vello-api` route — never
 * the realm (kernel `@vello/identity`; the M11_FIX_02 audit removed realm-as-authority). So the client route
 * guard here enforces only the meaningful *client-side* isolation — an operator (not a consumer) session — and
 * the server is the authority on whether that operator is actually an admin (a non-admin operator loads the
 * shell but every privileged call answers 403). Requiring an `ADMIN` realm the server never issues is exactly
 * the inconsistency this shell used to carry: `/v1/operator/login` mints MERCHANT/BUSINESS, so it could never
 * satisfy an `ADMIN`-realm guard.
 */
import {
  ApiClient,
  AuthApi,
  OperatorAuthApi,
  OpsApi,
  SetupReadinessApi,
  RideExecutionApi,
  FinanceApi,
  PartnerBillingAdminApi,
  PartnerAccountAdminApi,
  PartnerCostControlAdminApi,
  CourierDispatchAdminApi,
  CentralDeskApi,
  CentralTeamsApi,
  CentralWhatsappApi,
  AgentDesktopApi,
  AiSpendApi,
  VeloApi,
  SupportDeskApi,
  CentralCasesApi,
  SettingsApi,
  RefundApprovalsApi,
  CashHandoverApi,
  WalletAdminApi,
  IncentiveFundAdminApi,
  ActiveIncentivesAdminApi,
  IncentiveCockpitAdminApi,
  IncentiveControllerAdminApi,
  CouponCampaignsAdminApi,
  CouponAdminApi,
  DriverReferralAdminApi,
  SmartCampaignsAdminApi,
  SettlementAdminApi,
  DriverPaymentMethodsAdminApi,
  CaseAdminApi,
  ConfigApi,
  MarketsApi,
  AdminPlacesApi,
  AdminPlaceFeedbackApi,
  GeoApi,
  OperatorsAdminApi,
  ManualAccessAdminApi,
  AccessProfilesApi,
  AccountRegistryApi,
  IntegrationsApi,
  WidgetAdminApi,
  SecuritySettingsApi,
  FoodMerchantApi,
  MerchantConsoleApi,
  FleetManagementApi,
  AdminUsersApi,
  PersonInsightsApi,
  AdminIdentityVerificationApi,
  AdminRiderPhotoApi,
  ContextualSafetyApi,
  RiderLoyaltyApi,
  AdminKycApi,
  KycCatalogAdminApi,
  ServiceCategoriesApi,
  DriverEnablementAdminApi,
  DriverRegistryAdminApi,
  ProviderServicesAdminApi,
  ReputationAdminApi,
  GrowthApi,
  RideCollaborationApi,
  SafetyApi,
  LegalAdminApi,
  CareerAdminApi,
  SafetyEvidenceApi,
  AnalyticsApi,
  AnalyticsBiApi,
  RiderDebtsApi,
  CustomerInsightsApi,
  RiderCashReleaseAdminApi,
  RiderWalletAdminApi,
  AnalyticsListsApi,
  AccessReportApi,
  NotificationAdminApi,
  FoodOpsApi,
  FoodOnboardingReviewApi,
  FoodAdsGovernanceApi,
  FoodFulfillmentConfigApi,
  RideSupportApi,
  DeliveryApi,
  DeliveryOpsApi,
  FoodApi,
  FoodPostsaleApi,
  MerchantFinanceApi,
  PricingAutomationApi,
  StoreProfileApi,
  FoodCatalogApi,
  BroadcastApi,
  BannersApi,
  AwardsApi,
  OPERATOR_REALMS,
  isOperatorRealm,
  type OperatorLoginInput,
  type AuthRealm,
  type HttpTransport,
  type ApiError,
  type DeviceProofProvider,
} from '@vello/api-client';
import {
  SessionStore,
  establishSharedSession,
  type CrossContextLock,
  type SessionChangeSignal,
  type SecureStore,
  type SessionState,
  type RouteGraph,
  type RouteGuard,
  type UpdatePolicy,
  type NetworkStatus,
  type ConsentState,
  type AppBootState,
  bootApp,
  authGuard,
  checkRouteAccess,
  type NavContext,
} from '@vello/app-shell';
import { type Result } from '@vello/core';

/**
 * The realms that may open an Admin console route. The console rides an operator session, so this is exactly
 * the operator realm set (MERCHANT/BUSINESS) — the client-side gate is "operator, not consumer". Actual
 * platform-admin authority is server-authoritative (scoped `admin_role_grants` + AAL2), so this guard never
 * tries to encode admin-ness as a realm.
 */
export const ADMIN_CONSOLE_REALMS: readonly AuthRealm[] = OPERATOR_REALMS;

export const ADMIN_ROUTES: RouteGraph = {
  routes: {
    login: { name: 'login', requiresAuth: false },
    recovery: { name: 'recovery', requiresAuth: false },
    recovery_reset: { name: 'recovery_reset', requiresAuth: false },
    // D449 (A-05/A-06): the invitation landing — the invited operator sets its own authenticator and password.
    invitation: { name: 'invitation', requiresAuth: false },
    operators: { name: 'operators', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D475 (A-018/A-019, E-001/E-005, X-09): custom access profiles; the rider registration and the assisted phone change.
    access_profiles: { name: 'access_profiles', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D597 (MAN-ACCESS, ADR-0120): who may read the closed sections of the platform manual.
    manual_access: { name: 'manual_access', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    passenger_registry: { name: 'passenger_registry', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D513 (C-031): the Prestadores and Clientes dashboards — counts the directory already exposes.
    providers_overview: { name: 'providers_overview', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    customers_overview: { name: 'customers_overview', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    my_access: { name: 'my_access', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D806: "Meu perfil" - every console operator's own page; no permission word (the routes are "own account").
    my_profile: { name: 'my_profile', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    session_expired: { name: 'session_expired', requiresAuth: false },
    dashboard: { name: 'dashboard', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    live_map: { name: 'live_map', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    rides: { name: 'rides', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    ride_detail: { name: 'ride_detail', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    dispatch: { name: 'dispatch', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    courier_jobs: { name: 'courier_jobs', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    cases: { name: 'cases', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    disputes: { name: 'disputes', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    safety: { name: 'safety', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    driver_directory: { name: 'driver_directory', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    driver_detail: { name: 'driver_detail', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    driver_approvals: { name: 'driver_approvals', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    driver_documents: { name: 'driver_documents', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W8-DRIVER-ELIGIBILITY (D437): the market's driver-registered vehicles and their operator inspection.
    driver_vehicles: { name: 'driver_vehicles', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W9-DRIVER-REGISTRY (D481): the registration grid, the registration form and the central document query.
    driver_registry: { name: 'driver_registry', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    driver_register: { name: 'driver_register', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    driver_documents_all: { name: 'driver_documents_all', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D883 (program D871 phase 5): the review queue of the provider service lines.
    provider_services: { name: 'provider_services', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W9-REPUTATION (D478): suspensions with a start and an end, and the relationships console.
    reputation_suspensions: { name: 'reputation_suspensions', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    reputation_relationships: { name: 'reputation_relationships', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    passengers: { name: 'passengers', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    passenger_detail: { name: 'passenger_detail', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    companies: { name: 'companies', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    finance: { name: 'finance', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    finance_payouts: { name: 'finance_payouts', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    finance_partner_billing: { name: 'finance_partner_billing', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    tariff_categories: { name: 'tariff_categories', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    tariff_dynamic: { name: 'tariff_dynamic', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W9-CATEGORY-CATALOG (D462): the governed service-category catalog.
    service_categories: { name: 'service_categories', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    campaigns: { name: 'campaigns', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    registries: { name: 'registries', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    markets: { name: 'markets', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    integrations: { name: 'integrations', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    security: { name: 'security', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    governed_settings: { name: 'governed_settings', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D631 (Digital Central phase 0, doc 25 section 11): the assistant, its refund caps and payment options, the AI models, speech engine and budget.
    central_ai_settings: { name: 'central_ai_settings', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D639 (Digital Central phase 4, doc 25 section 11): "Gastos de IA" - what the AI cost this month, the forecast and the budget status.
    ai_spend: { name: 'ai_spend', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D874: "Gastos de mapas" - what Google and HERE bill this month, by SKU, city and purpose, with the forecast (no budget, no invoice entry).
    maps_spend: { name: 'maps_spend', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W8-DRIVER-ELIGIBILITY (D437): a country's KYC document-type catalog under four eyes.
    kyc_catalog: { name: 'kyc_catalog', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    integrations_otp_setup: { name: 'integrations_otp_setup', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D429 W-EMBED-ADMIN: the Central widget's embeds (create, settings, copy the snippet, rotate, revoke).
    integrations_widgets: { name: 'integrations_widgets', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D440 (X-02): the versioned terms of use / privacy policy per country (four-eyes publish).
    legal_documents: { name: 'legal_documents', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // The institutional site's job openings ("vagas"): written per country / city, opened and closed from here.
    career_openings: { name: 'career_openings', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    notifications: { name: 'notifications', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    analytics: { name: 'analytics', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    audit: { name: 'audit', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    food_ops: { name: 'food_ops', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    food_review: { name: 'food_review', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    food_ads: { name: 'food_ads', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    food_fulfillment: { name: 'food_fulfillment', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W6-ADMIN-SCREENS (D401): the consoles whose server routes already existed and had no screen.
    food_orders: { name: 'food_orders', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    food_postsale: { name: 'food_postsale', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    food_stores: { name: 'food_stores', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    send_deliveries: { name: 'send_deliveries', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    send_claims: { name: 'send_claims', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    finance_merchant_statements: { name: 'finance_merchant_statements', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W6-REFUND-FOUR-EYES (D398) had no route-graph entry; W7-CASH-HANDOVER-CONSOLE (D411) adds it with its own two.
    finance_refund_approvals: { name: 'finance_refund_approvals', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    finance_cash_handovers: { name: 'finance_cash_handovers', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    finance_driver_debts: { name: 'finance_driver_debts', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D438 (A-02)
    finance_rider_debts: { name: 'finance_rider_debts', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D536
    finance_rider_cash_blocks: { name: 'finance_rider_cash_blocks', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D536
    finance_wallets: { name: 'finance_wallets', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D459
    finance_rider_wallet: { name: 'finance_rider_wallet', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D737b
    finance_incentive_fund: { name: 'finance_incentive_fund', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D489b
    finance_incentive_offers: { name: 'finance_incentive_offers', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D490
    finance_incentive_controller: { name: 'finance_incentive_controller', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D495
    finance_coupon_campaigns: { name: 'finance_coupon_campaigns', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D493
    referrals: { name: 'referrals', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D723: Indicações (rider and driver referral campaigns)
    finance_settlement: { name: 'finance_settlement', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D469
    driver_groups: { name: 'driver_groups', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS }, // D459
    four_eyes_inbox: { name: 'four_eyes_inbox', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D615 (SETUP-READINESS): Configurações › Configuração da plataforma.
    platform_setup: { name: 'platform_setup', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // W7-OPS-GOVERNANCE (D420): the OPS incident queue (read only; the owner decision on D416).
    ops_incidents: { name: 'ops_incidents', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D472 (A-11, P-07, A-14): the ride grid, the report catalogue and the global audit trail.
    rides_grid: { name: 'rides_grid', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    report_catalog: { name: 'report_catalog', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    audit_trail: { name: 'audit_trail', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D471 (A-29): the route-anomaly console.
    route_anomalies: { name: 'route_anomalies', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D474: the risk areas (propose / approve) and the alert center.
    risk_areas: { name: 'risk_areas', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    alert_center: { name: 'alert_center', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D488 (W10-MARKET-SIGNALS): the incentive zones (propose / approve).
    incentive_zones: { name: 'incentive_zones', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D616 (GEOFENCE-EDITOR, ADR-0121): the market's service area (draw / submit / approve and publish).
    service_areas: { name: 'service_areas', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D674 (doc 27): the shared base of addresses and named places - a list with a filter per column, manual create / edit / hide / delete, the bulk clears.
    places: { name: 'places', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D561 (ADR-0116): the devices of a market (an operations-room TV, a kiosk): register, pair, revoke.
    devices: { name: 'devices', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D647 (ADR-0123): the wall screens of the operations room - a link per TV with its views and scope.
    tv_panels: { name: 'tv_panels', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D477: the mass broadcast, the notification banners and the driver award campaigns.
    broadcasts: { name: 'broadcasts', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    banners: { name: 'banners', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    awards: { name: 'awards', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D473: the central's side of the ride chat (conversations, thread, ready messages).
    ride_chats: { name: 'ride_chats', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D632 (doc 25 section 7, Q-367): the teams of the Digital Central per country - function, languages, channels, local hours, members.
    central_teams: { name: 'central_teams', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D636 (doc 25 phase 1, Q-365): the WhatsApp business numbers of the Central - the country each serves, its templates and its three secrets.
    central_whatsapp: { name: 'central_whatsapp', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D522 (AN-BI-UI, doc 20 §4.3): Análises — the standard panels, the explorer and the operator's saved reports.
    bi_panels: { name: 'bi_panels', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    bi_explorer: { name: 'bi_explorer', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    bi_reports: { name: 'bi_reports', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D532 (AN-EXPORTS, doc 20 §8): the asynchronous exports, their schedules and templates.
    bi_exports: { name: 'bi_exports', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    // D527 (doc 20 section 7): the pending reports - the lists (driver km, customers, companies, requests, quotes by provider, ads) and the access report.
    bi_lists: { name: 'bi_lists', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    access_report: { name: 'access_report', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
    about: { name: 'about', requiresAuth: true, requiresRealm: ADMIN_CONSOLE_REALMS },
  },
};

export const ADMIN_GUARDS: readonly RouteGuard[] = [authGuard('login'), (ctx) => ctx.accountRoute];

export interface AdminRuntime {
  readonly sessionStore: SessionStore;
  readonly client: ApiClient;
  /** The resolved Vello API origin this console talks to (for the public `/v1/version` read in Sobre). */
  readonly apiBaseUrl: string;
  readonly auth: AuthApi;
  readonly operatorAuth: OperatorAuthApi;
  readonly ops: OpsApi;
  /** D615 (SETUP-READINESS): what is still missing for the platform to work end to end. */
  readonly setupReadiness: SetupReadinessApi;
  /** D477 (ADR-0095): the mass broadcast, the notification banners and the driver award campaigns. */
  readonly broadcast: BroadcastApi;
  readonly banners: BannersApi;
  readonly awards: AwardsApi;
  readonly execution: RideExecutionApi;
  readonly finance: FinanceApi;
  readonly cases: CaseAdminApi;
  readonly config: ConfigApi;
  /** Operating-market registry (Layer 2; m0101) — the Mercados screen + the scope selector's catalog. */
  readonly markets: MarketsApi;
  /** D674 (doc 27): the shared places base - the Endereços screen (list, manual create / edit / hide / delete, bulk clears). */
  readonly places: AdminPlacesApi;
  /** D704 (doc 28 gap G6): the riders' place suggestions and corrections - the Sugestões tab of Endereços (accept, merge, reject). */
  readonly placeFeedback: AdminPlaceFeedbackApi;
  /** D674: the postal-code format of a market's country (its label comes from the country pack) - the Endereços screen names the field the way the country does. */
  readonly geo: GeoApi;
  /** D449 (A-05): the operator directory — invite, grant, revoke, deactivate, end sessions, reset MFA, the four-eyes queue. */
  readonly operatorsAdmin: OperatorsAdminApi;
  readonly accessProfiles: AccessProfilesApi;
  /** D597 (MAN-ACCESS): the grants that open the closed sections of the platform manual — list, grant, revoke. */
  readonly manualAccess: ManualAccessAdminApi;
  readonly accountRegistry: AccountRegistryApi;
  readonly integrations: IntegrationsApi;
  /** The Central widget's embeds (D429): `/v1/config/widgets*` under `widget:view` / `widget:manage`. */
  readonly widgets: WidgetAdminApi;
  /** Governed security settings (D342) — the Segurança screen (runtime-editable session-revocation TTL). */
  readonly securitySettings: SecuritySettingsApi;
  readonly foodMerchant: FoodMerchantApi;
  readonly merchantConsole: MerchantConsoleApi;
  readonly fleetManagement: FleetManagementApi;
  readonly adminUsers: AdminUsersApi;
  /** D580 (A2): the single-person reads of the person record - relationships, support tickets, safety incidents, legal acceptances and privacy requests. */
  readonly personInsights: PersonInsightsApi;
  /** D726: a rider's verification badge and attempts (`account:view`), and the images with the privacy permission (audited). */
  readonly identityVerification: AdminIdentityVerificationApi;
  /** D725: the rider's profile photo on the person record - state and history, the image (audited), the four-eyes rejection. */
  readonly riderPhoto: AdminRiderPhotoApi;
  /** D580 (A2): a person's safety flags (`listFlags(subjectId)`) for the person record. */
  readonly contextualSafety: ContextualSafetyApi;
  /** D580 (A2): a rider's loyalty accounts and subscriptions (`listAccounts(riderId)`, `listSubscriptions(riderId)`) for the person record. */
  readonly riderLoyalty: RiderLoyaltyApi;
  readonly adminKyc: AdminKycApi;
  /** D437: a country's KYC document-type catalog under four eyes; the market's drivers, documents and vehicle inspections. */
  readonly kycCatalog: KycCatalogAdminApi;
  /** D462: the governed service-category catalog (versions under four eyes, status switch, copy to markets). */
  readonly serviceCategories: ServiceCategoriesApi;
  readonly driverEnablement: DriverEnablementAdminApi;
  /** D481: the driver registration (form, grid, record, evidence) and the central document query. */
  readonly driverRegistry: DriverRegistryAdminApi;
  /** D883 (program D871 phase 5): the review of the provider service lines and the vehicle approvals per line. */
  readonly providerServices: ProviderServicesAdminApi;
  /** D478: suspensions with a start and an end, occurrences, the relationships console and the level criteria in force. */
  readonly reputation: ReputationAdminApi;
  readonly growth: GrowthApi;
  /** Dispute triage (Disputas) — the M13 collaboration surface's operator side. */
  readonly rideCollaboration: RideCollaborationApi;
  /** Incident detail read (Segurança e risco); triage transitions ride on `cases`. */
  readonly safety: SafetyApi;
  /** D440 (X-02): the versioned legal documents (four-eyes publish). */
  readonly legal: LegalAdminApi;
  /** The institutional site's job openings (`/v1/admin/career-openings*`, `career_opening:view` / `:manage` at the opening's country). */
  readonly careers: CareerAdminApi;
  readonly safetyEvidence: SafetyEvidenceApi;
  readonly analytics: AnalyticsApi;
  /** D522 (AN-BI-UI): Vello's own BI — the governed catalog, the query and the CSV (`/v1/analytics/{catalog,query,export}`). */
  readonly analyticsBi: AnalyticsBiApi;
  /** D536 (AN-RIDER-DEBTS): the riders in debt and the riders blocked for cash (`/v1/analytics/rider-debts/*`, `/v1/admin/rider-cash-blocks`). */
  readonly riderDebts: RiderDebtsApi;
  /** D580: the passenger record's activity summary - trips, cancellations, spend, ratings, promotions from the analytics store (`/v1/analytics/customers/:id/summary`). */
  readonly customerInsights: CustomerInsightsApi;
  /** D501 / D536: the four-eyes release of a rider's cash block (`/v1/admin/rider-cash-releases`). */
  readonly riderCashReleases: RiderCashReleaseAdminApi;
  /** D737a (ADR-0150): a rider's signed balance, the maker/checker adjustments and the approvals inbox (`/v1/ops/riders/:id/wallet*`, `/v1/ops/rider-wallet/adjustments*`). */
  readonly riderWallet: RiderWalletAdminApi;
  /** D527: the pending reports - the Admin's lists over the analytics store (`/v1/analytics/lists/:list`) and the access report (`/v1/ops/access/sessions`). */
  readonly analyticsLists: AnalyticsListsApi;
  readonly accessReport: AccessReportApi;
  readonly notificationsAdmin: NotificationAdminApi;
  /** Food control-plane consoles (D321): platform ops, onboarding review, ads governance, fulfillment policy. */
  readonly foodOps: FoodOpsApi;
  readonly foodOnboardingReview: FoodOnboardingReviewApi;
  readonly foodAdsGovernance: FoodAdsGovernanceApi;
  readonly foodFulfillmentConfig: FoodFulfillmentConfigApi;
  /** Support ride search + ride view (D358, `ride:view`). */
  readonly rideSupport: RideSupportApi;
  /** Partner billing (D368): accounts' exposure, statements, early close, four-eyes payment. */
  readonly partnerBilling: PartnerBillingAdminApi;
  /** D446 (ADR-0073): register and maintain partner accounts, their four-eyes line and desk members. */
  readonly partnerAccounts: PartnerAccountAdminApi;
  /** D479 (ADR-0097): cost centers, monthly limits, billing model, category scope and quick-order zones of a partner account. */
  readonly partnerCostControl: PartnerCostControlAdminApi;
  /** Couriers on jobs (D378): a market's courier jobs with the stuck signals, and the reason-coded courier release. */
  readonly courierDispatch: CourierDispatchAdminApi;
  /** D473: the operator's side of the ride chat, the courier incidents queue (and the central's new order). */
  readonly centralDesk: CentralDeskApi;
  /** D632: the Central teams (`/v1/central/teams*`) - list, edit hours and languages, archive, members. */
  readonly centralTeams: CentralTeamsApi;
  /** D636: the Central's WhatsApp numbers (`/v1/central/whatsapp/numbers*`) - register, edit, set the secrets, activate, pause, retire. */
  readonly centralWhatsapp: CentralWhatsappApi;
  /** D419: the desk agents a supervisor may name (`GET /v1/agent-desktop/agents`) - the member picker of a team. */
  readonly agentDesktop: AgentDesktopApi;
  /** D639: the AI spend panel (`/v1/ai/spend`, `/v1/ai/fx-rates`) and what Vello can do (`/v1/velo/intents`, for the autonomy form). */
  readonly aiSpend: AiSpendApi;
  readonly velo: VeloApi;
  /** The support desk (D384): open for a caller, subject card, notes, reveal, resolve/close, Finance's flags. */
  readonly supportDesk: SupportDeskApi;
  /** D637 (the one case model): which Atendimento in the Central holds a record (`GET /v1/central/case-links`) - the "em atendimento na Central" badge. */
  readonly centralCases: CentralCasesApi;
  // ---- W6-ADMIN-SCREENS (D401): the endpoint groups the console never wired -----------------------
  /** Food orders (`GET /v1/food/orders`): the operator worklist, the order detail and the operator cancel. */
  readonly food: FoodApi;
  /** Send deliveries: the per-market worklist and one parcel's custody chain. */
  readonly delivery: DeliveryApi;
  /** Send claims (`claim:*`): the open queue, one claim's evidence and audit, and the reason-coded decision. */
  readonly deliveryOps: DeliveryOpsApi;
  /** Food post-sale cases (`postsale:*`): cancellations, refunds and disputes with the server's split. */
  readonly foodPostsale: FoodPostsaleApi;
  /** Storefront operating profiles: publication, availability, hours and the operator pause/resume. */
  readonly storeProfile: StoreProfileApi;
  /** D446 (M-12): the store-live flow publishes a new store's initial menu (an operator holding `catalog:publish`). */
  readonly foodCatalog: FoodCatalogApi;
  /** Merchant statements and payouts: what the ledger owes, and the four-eyes mark-paid. */
  readonly merchantFinance: MerchantFinanceApi;
  /** Dynamic-pricing automation: the kill switch and the decision log. */
  readonly pricingAutomation: PricingAutomationApi;
  /** Governed settings (D400): the four-eyes console over the D393 kernel (propose / approve / rollback / history). */
  readonly settings: SettingsApi;
  /** Refunds and credits above the governed threshold (D398): the Finance queue and the compensation proposal. */
  readonly refundApprovals: RefundApprovalsApi;
  /** The courier's cash handover under four eyes (D411; the D392 A2 routes): record, queue, confirm/reject. */
  readonly cashHandovers: CashHandoverApi;
  /** D459 (ADR-0074): the prepaid wallets (packages, standings, four-eyes changes) and the driver groups. */
  readonly wallet: WalletAdminApi;
  /** D489b (ADR-0102): the Incentive Fund's balances and the owner's four-eyes investment. */
  readonly incentiveFund: IncentiveFundAdminApi;
  /** D490 (ADR-0106): the incentive offers (propose, four-eyes decide, end early, list). */
  readonly activeIncentives: ActiveIncentivesAdminApi;
  /** D495 (ADR-0112): the incentive controller (mode and policy, decision log, kill switch with four-eyes re-enable). */
  readonly incentiveController: IncentiveControllerAdminApi;
  /** D497: the cockpit's read aggregations (spend curve vs plan, zone state, policy, preview). */
  readonly incentiveCockpit: IncentiveCockpitAdminApi;
  /** D493 (ADR-0109): the targeted coupon campaigns (propose, four-eyes decide with the reservation, end, restart, list). */
  readonly couponCampaigns: CouponCampaignsAdminApi;
  /** D723 (doc 29 G12): Indicações - the rider-to-rider referral campaigns (D476) and the four-eyes driver invitation campaigns. */
  readonly referralCampaigns: CouponAdminApi;
  readonly driverReferrals: DriverReferralAdminApi;
  /** D494 (ADR-0111): the merchants' Smart Campaigns - the platform's co-investment and spend, and the kill switch. */
  readonly smartCampaigns: SmartCampaignsAdminApi;
  /** D469 (ADR-0087): the driver settlement modes. */
  readonly settlement: SettlementAdminApi;
  /** D470 (F-006): a driver's payment methods and the operator's veto. */
  readonly driverPaymentMethods: DriverPaymentMethodsAdminApi;
}

export function createAdminRuntime(input: {
  readonly transport: HttpTransport;
  readonly secureStore: SecureStore;
  readonly baseUrl: string;
  readonly correlationId?: () => string;
  /** Device-key proof provider (ADR-0039): binds the operator session's refresh to this browser's key. */
  readonly deviceProof?: DeviceProofProvider;
}): AdminRuntime {
  const sessionStore = new SessionStore(input.secureStore);
  const client = new ApiClient({
    transport: input.transport,
    baseUrl: input.baseUrl,
    accessToken: () => sessionStore.accessToken(),
    ...(input.correlationId !== undefined ? { correlationId: input.correlationId } : {}),
    ...(input.deviceProof !== undefined ? { deviceProof: input.deviceProof } : {}),
  });
  return {
    sessionStore,
    client,
    apiBaseUrl: input.baseUrl,
    auth: new AuthApi(client),
    operatorAuth: new OperatorAuthApi(client),
    ops: new OpsApi(client),
    setupReadiness: new SetupReadinessApi(client),
    broadcast: new BroadcastApi(client),
    banners: new BannersApi(client),
    awards: new AwardsApi(client),
    execution: new RideExecutionApi(client),
    finance: new FinanceApi(client),
    cases: new CaseAdminApi(client),
    config: new ConfigApi(client),
    markets: new MarketsApi(client),
    places: new AdminPlacesApi(client),
    placeFeedback: new AdminPlaceFeedbackApi(client),
    geo: new GeoApi(client),
    operatorsAdmin: new OperatorsAdminApi(client),
    accessProfiles: new AccessProfilesApi(client),
    manualAccess: new ManualAccessAdminApi(client),
    accountRegistry: new AccountRegistryApi(client),
    integrations: new IntegrationsApi(client),
    widgets: new WidgetAdminApi(client),
    securitySettings: new SecuritySettingsApi(client),
    foodMerchant: new FoodMerchantApi(client),
    merchantConsole: new MerchantConsoleApi(client),
    fleetManagement: new FleetManagementApi(client),
    adminUsers: new AdminUsersApi(client),
    personInsights: new PersonInsightsApi(client),
    identityVerification: new AdminIdentityVerificationApi(client),
    riderPhoto: new AdminRiderPhotoApi(client),
    contextualSafety: new ContextualSafetyApi(client),
    riderLoyalty: new RiderLoyaltyApi(client),
    adminKyc: new AdminKycApi(client),
    kycCatalog: new KycCatalogAdminApi(client),
    serviceCategories: new ServiceCategoriesApi(client),
    driverEnablement: new DriverEnablementAdminApi(client),
    driverRegistry: new DriverRegistryAdminApi(client),
    providerServices: new ProviderServicesAdminApi(client),
    reputation: new ReputationAdminApi(client),
    growth: new GrowthApi(client),
    rideCollaboration: new RideCollaborationApi(client),
    safety: new SafetyApi(client),
    legal: new LegalAdminApi(client),
    careers: new CareerAdminApi(client),
    safetyEvidence: new SafetyEvidenceApi(client),
    analytics: new AnalyticsApi(client),
    analyticsBi: new AnalyticsBiApi(client),
    riderDebts: new RiderDebtsApi(client),
    customerInsights: new CustomerInsightsApi(client),
    riderCashReleases: new RiderCashReleaseAdminApi(client),
    riderWallet: new RiderWalletAdminApi(client),
    analyticsLists: new AnalyticsListsApi(client),
    accessReport: new AccessReportApi(client),
    notificationsAdmin: new NotificationAdminApi(client),
    foodOps: new FoodOpsApi(client),
    foodOnboardingReview: new FoodOnboardingReviewApi(client),
    foodAdsGovernance: new FoodAdsGovernanceApi(client),
    foodFulfillmentConfig: new FoodFulfillmentConfigApi(client),
    rideSupport: new RideSupportApi(client),
    partnerBilling: new PartnerBillingAdminApi(client),
    partnerAccounts: new PartnerAccountAdminApi(client),
    partnerCostControl: new PartnerCostControlAdminApi(client),
    courierDispatch: new CourierDispatchAdminApi(client),
    centralDesk: new CentralDeskApi(client),
    centralTeams: new CentralTeamsApi(client),
    centralWhatsapp: new CentralWhatsappApi(client),
    agentDesktop: new AgentDesktopApi(client),
    aiSpend: new AiSpendApi(client),
    velo: new VeloApi(client),
    supportDesk: new SupportDeskApi(client),
    centralCases: new CentralCasesApi(client),
    food: new FoodApi(client),
    delivery: new DeliveryApi(client),
    deliveryOps: new DeliveryOpsApi(client),
    foodPostsale: new FoodPostsaleApi(client),
    storeProfile: new StoreProfileApi(client),
    foodCatalog: new FoodCatalogApi(client),
    merchantFinance: new MerchantFinanceApi(client),
    pricingAutomation: new PricingAutomationApi(client),
    settings: new SettingsApi(client),
    refundApprovals: new RefundApprovalsApi(client),
    cashHandovers: new CashHandoverApi(client),
    wallet: new WalletAdminApi(client),
    incentiveFund: new IncentiveFundAdminApi(client),
    activeIncentives: new ActiveIncentivesAdminApi(client),
    incentiveController: new IncentiveControllerAdminApi(client),
    incentiveCockpit: new IncentiveCockpitAdminApi(client),
    couponCampaigns: new CouponCampaignsAdminApi(client),
    referralCampaigns: new CouponAdminApi(client),
    driverReferrals: new DriverReferralAdminApi(client),
    smartCampaigns: new SmartCampaignsAdminApi(client),
    settlement: new SettlementAdminApi(client),
    driverPaymentMethods: new DriverPaymentMethodsAdminApi(client),
  };
}

export interface BootAdminOptions {
  readonly currentVersion: string;
  readonly updatePolicy: UpdatePolicy;
  readonly network: NetworkStatus;
  readonly accountRoute?: (session: SessionState) => string | undefined;
}

export function bootAdmin(runtime: AdminRuntime, opts: BootAdminOptions): Promise<AppBootState> {
  return bootApp({
    sessionStore: runtime.sessionStore,
    currentVersion: opts.currentVersion,
    updatePolicy: opts.updatePolicy,
    // Operators have granted (implicit) telemetry consent for the operator tools.
    consent: 'granted' as ConsentState,
    network: opts.network,
    guards: ADMIN_GUARDS,
    fallbackRoute: 'dashboard',
    ...(opts.accountRoute !== undefined ? { accountRoute: opts.accountRoute } : {}),
  });
}

/**
 * Authenticate an operator (credential + MFA → AAL2) and establish the session under the operator realm the
 * server actually issued.
 *
 * **Fail-closed.** The login response carries only a session id, never the operator identity or realms, so the
 * token is *provisionally* activated only long enough for `/v1/me` to resolve the real profile. The session is
 * committed under the operator realm from `/me` (MERCHANT or BUSINESS) **only** when `/me` succeeds **and** the
 * profile actually carries an operator realm. If `/me` fails, or the credential resolves to a consumer
 * (RIDER/DRIVER) profile with no operator realm, the provisional session is torn down and the login is rejected
 * — a token is never trusted as an operator on the strength of a login response alone, and a `/me` failure
 * never leaves a usable session (no fail-open, no realm forgery). Whether that operator is a platform admin is
 * decided server-side (scoped `admin_role_grants` + AAL2) on each privileged call, not here.
 */
export async function establishOperatorSession(runtime: AdminRuntime, input: OperatorLoginInput,
  coordination: { readonly lock?: CrossContextLock; readonly signal?: SessionChangeSignal } = {},
): Promise<Result<SessionState, ApiError>> {
  // ADR-0039 amendment 3 (Codex r3 P1-2): the login runs inside the shared logout-generation protocol — the generation is
  // captured before the request, the provisional bearer is only STAGED in this tab's memory (never visible to other tabs),
  // and the session commits under the session lock only if no logout happened meanwhile (else it is revoked server-side).
  return establishSharedSession({
    sessionStore: runtime.sessionStore,
    auth: runtime.auth,
    deviceFingerprint: input.deviceFingerprint,
    ...(coordination.lock !== undefined ? { lock: coordination.lock } : {}),
    ...(coordination.signal !== undefined ? { signal: coordination.signal } : {}),
    mint: async () => {
      const login = await runtime.operatorAuth.login(input);
      if (!login.ok) return login;
      const base = {
        tokenType: login.value.tokenType,
        accessToken: login.value.accessToken,
        refreshToken: login.value.refreshToken,
        accessTokenExpiresAt: login.value.accessTokenExpiresAt,
        sessionId: login.value.sessionId,
        created: false,
      };
      // Provisionally stage the token ONLY so `/v1/me` can be called; its realm is a throwaway bootstrap value (the committed
      // realm comes from `/me`). A `/me` failure or a non-operator profile discards it — nothing is persisted (fail-closed).
      runtime.sessionStore.stage({ ...base, userId: login.value.sessionId }, OPERATOR_REALMS[0]);
      const me = await runtime.auth.me();
      if (!me.ok) return me;
      const sessionRealm = me.value.realms.find((r): r is AuthRealm => isOperatorRealm(r));
      if (sessionRealm === undefined) {
        return { ok: false, error: { code: 'FORBIDDEN', status: 403, detail: 'operator profile does not carry an operator realm' } };
      }
      return { ok: true, value: { session: { ...base, userId: me.value.id }, realm: sessionRealm } };
    },
  });
}

// ---- operator scope (global > country > state > city) ---------------------------------------------

/**
 * The scope the console is looking at. Hierarchy (STATE-SCOPE S2, ADR-0124): global `{}` > country `{ country }` > state
 * `{ country, region }` > city `{ country, city }`. `region` is the ISO 3166-2 code (`BR-MG`). A **city scope never
 * carries a region** (it is `country` + `city` as before, so a scope stored by an older console stays valid); a state
 * scope is "a country restricted to the markets of that state" - see `marketsOfScope` in `scope.ts`.
 */
export interface OperatorScope {
  readonly country?: string;
  readonly region?: string;
  readonly city?: string;
}

export interface ScopeState {
  readonly current: OperatorScope;
  /** The scopes this operator is granted; a switch outside this set is refused. */
  readonly allowed: readonly OperatorScope[];
}

function scopeEquals(a: OperatorScope, b: OperatorScope): boolean {
  return a.country === b.country && a.region === b.region && a.city === b.city;
}

/** Switch the active operator scope; refused (no-op) when the target is not one of the granted scopes. */
export function switchScope(state: ScopeState, target: OperatorScope): ScopeState {
  return state.allowed.some((s) => scopeEquals(s, target)) ? { ...state, current: target } : state;
}

// D401: `scopesFromMarkets` (the catalog-only scope list) was superseded by `scopesFromGrantsAndMarkets`
// (D364, `scope.ts`), which fuses the catalog with the operator's real grants. Only its own test still
// called it, so it was removed rather than left as a second, more generous answer to the same question.

// ---- inactivity + session expiry -----------------------------------------------------------------

/** Whether the operator session must re-authenticate: the access token is expired OR inactivity has elapsed. */
export function requiresReauth(input: {
  readonly sessionStore: SessionStore;
  readonly now: string;
  readonly lastActivityAt: string;
  readonly inactivityMs: number;
}): boolean {
  if (input.sessionStore.snapshot().status !== 'authenticated') return true;
  if (input.sessionStore.needsRefresh(input.now, 0)) return true;
  const idle = Date.parse(input.now) - Date.parse(input.lastActivityAt);
  return Number.isNaN(idle) ? true : idle >= input.inactivityMs;
}

/** Deny-by-default route access for the operator console (re-exports the app-shell guard for admin use). */
export function checkAdminRoute(name: string, ctx: NavContext): ReturnType<typeof checkRouteAccess> {
  return checkRouteAccess(ADMIN_ROUTES, name, ctx);
}
